import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { CadastroContratoAssinadoField } from '@/components/ClientesAgrada/CadastroContratoAssinadoField'
import {
  ClientesAgradaCadastroFields,
  emptyCadastroClienteForm,
  type CadastroClienteForm,
} from '@/components/ClientesAgrada/ClientesAgradaCadastroFields'
import { clientesService, type ClienteVendedorOption } from '@/services/clientesService'
import { financeService } from '@/services/financeService'

function todayKey() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

function money(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function sellerLabel(role: string) {
  if (role === 'CLOSER') return 'Vendedor'
  if (role === 'MANAGER') return 'Gestor'
  if (role === 'ADMIN') return 'Admin'
  return role
}

function errorMessage(err: unknown) {
  const data = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
  return data?.error?.message || 'Não foi possível cadastrar o cliente'
}

export default function ClienteAgradaCadastroPage() {
  const navigate = useNavigate()
  const [vendedores, setVendedores] = useState<ClienteVendedorOption[]>([])
  const [loadingSellers, setLoadingSellers] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<CadastroClienteForm>(() => emptyCadastroClienteForm(todayKey()))
  const [contractFile, setContractFile] = useState<File | null>(null)
  const [signedContract, setSignedContract] = useState<{ file: File; signedAt: string } | null>(null)

  useEffect(() => {
    void clientesService
      .listVendedores()
      .then((rows) => {
        setVendedores(rows)
        setForm((current) => ({ ...current, vendedorId: rows[0]?.id || '' }))
      })
      .catch(() => toast.error('Não foi possível carregar os vendedores'))
      .finally(() => setLoadingSellers(false))
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const mode = form.contractDeliveryMode
    if (mode !== 'pdf' && mode !== 'link') {
      toast.error('Escolha o formato do contrato')
      return
    }
    if (!form.planId) {
      toast.error('Escolha o plano')
      return
    }
    const mensal = Number(String(form.valorMensal).replace(',', '.'))
    if (!Number.isFinite(mensal) || mensal <= 0) {
      toast.error('Informe o valor mensal')
      return
    }
    const link = form.contractSignatureLink.trim()
    if (link && !/^https?:\/\//i.test(link)) {
      toast.error('Informe um link válido (http ou https)')
      return
    }
    if (
      mode === 'link' &&
      (form.contractStatus === 'CONTRATO_ENVIADO' || form.contractStatus === 'CONTRATO_ASSINADO') &&
      !link
    ) {
      toast.error('Cole o link de assinatura')
      return
    }
    if (form.contractStatus === 'CONTRATO_ASSINADO' && !signedContract) {
      toast.error('Anexe o PDF assinado com certificado D4Sign')
      return
    }
    if (
      contractFile &&
      (contractFile.size > 10 * 1024 * 1024 ||
        (contractFile.type !== 'application/pdf' && !contractFile.name.toLowerCase().endsWith('.pdf')))
    ) {
      toast.error('Envie um PDF de até 10 MB')
      return
    }
    setSaving(true)
    try {
      const created = await clientesService.createManual({
        ...form,
        contractDeliveryMode: mode,
        contractSignatureLink: link,
        valorMensal: mensal,
        signedAt: signedContract?.signedAt || '',
      })
      const pdfToUpload = form.contractStatus === 'CONTRATO_ASSINADO' ? signedContract?.file : contractFile
      if (pdfToUpload) {
        const pdfType = form.contractStatus === 'CONTRATO_ASSINADO' ? 'comercial' : 'nao_assinado'
        try {
          await financeService.uploadContract(created.projectId, pdfToUpload, pdfType)
        } catch {
          toast.error('Cliente cadastrado, mas o PDF do contrato não foi anexado')
          navigate('/clientes')
          return
        }
      }
      const commission =
        created.comissaoCentavos != null
          ? ` Comissão do vendedor: ${money(created.comissaoCentavos)}.`
          : ''
      toast.success(`Cliente cadastrado como negócio ganho.${commission}`)
      navigate('/clientes')
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <DashboardLayout title='Cadastrar cliente'>
      <form className='grid items-stretch gap-4 xl:grid-cols-2' onSubmit={(event) => void submit(event)}>
        <div className='flex flex-wrap items-center justify-between gap-3 xl:col-span-2'>
          <p className='text-sm text-text-muted'>
            Cliente já fechado, com os dados do contrato e a comissão do vendedor.
          </p>
          <div className='flex shrink-0 gap-2'>
            <Link to='/clientes'>
              <Button variant='secondary' size='sm' type='button' disabled={saving}>
                <ArrowLeft size={14} />
                Voltar
              </Button>
            </Link>
            <Button type='submit' size='sm' disabled={saving || loadingSellers}>
              {saving ? 'Salvando…' : 'Cadastrar'}
            </Button>
          </div>
        </div>
        <ClientesAgradaCadastroFields
          form={form}
          sellers={vendedores}
          sellerLabel={sellerLabel}
          today={todayKey()}
          contractFileName={contractFile?.name ?? ''}
          signedContractField={<CadastroContratoAssinadoField disabled={saving} onResolved={setSignedContract} />}
          onContractFile={setContractFile}
          onChange={(patch) => {
            if (patch.contractStatus && patch.contractStatus !== 'CONTRATO_ASSINADO') {
              setSignedContract(null)
            }
            setForm((current) => ({ ...current, ...patch }))
          }}
        />
      </form>
    </DashboardLayout>
  )
}
